import { collection, doc, limit, onSnapshot, orderBy, query, where, type Unsubscribe } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase/config';
import type {
  BrainConfig,
  BrainVersion,
  ClaireActivity,
  ClaireApproval,
  ClaireMessage,
  ClaireIdentity,
  ClaireMeeting,
  ClaireMode,
  ClaireSession,
  ClaireSkill,
  ClaireWatch,
  ElevenVoice,
  KnowledgeEntry,
  KnowledgeVersion,
  MeetingLine,
  MeetingParticipation,
  MemoryEntry,
  ModelAssignment,
  ModelRole,
  PermissionsConfig,
  PresenceConfig,
  ToolMeta,
  ToolPolicy,
} from '../lib/claire/types';

const SESSIONS = 'claireSessions';
const byMillis = (a?: { toMillis?: () => number } | null) => a?.toMillis?.() || 0;

/**
 * A new conversation's session doc doesn't exist until the server creates it, so the rules
 * deny the first listen. Re-subscribe briefly until it appears.
 */
function retrying(subscribe: (onError: () => void) => Unsubscribe, attempts = 40, delayMs = 750): Unsubscribe {
  let stopped = false;
  let unsub: Unsubscribe = () => undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const start = (left: number) => {
    unsub = subscribe(() => {
      if (stopped || left <= 0) return;
      timer = setTimeout(() => !stopped && start(left - 1), delayMs);
    });
  };
  start(attempts);
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    unsub();
  };
}

export interface ClaireBootstrap {
  brain: { config: BrainConfig; version: number };
  models: Record<ModelRole, ModelAssignment>;
  permissions: PermissionsConfig;
  policies: Record<string, ToolPolicy>;
  tools: ToolMeta[];
  presence: PresenceConfig;
  finAreas: { id: string; label: string; paths: string[] }[];
}

const voiceFn = () => httpsCallable<unknown, any>(functions, 'claireVoice', { timeout: 60_000 });
const playBase64 = (audio: string, contentType: string) => {
  const el = new Audio(`data:${contentType};base64,${audio}`);
  void el.play();
  return el;
};

export interface ClaireReply {
  sessionId: string;
  text: string;
  status: string;
  activityId: string | null;
  toolCalls: { name: string; outcome: string }[];
}

export interface ZoomStatus {
  connected: boolean;
  email: string | null;
  personalMeetingUrl: string | null;
  meetings: { topic: string; startTime: string | null; joinUrl: string }[];
}

export interface ClaireCalendarEvent {
  id: string;
  title: string;
  start: string | null;
  end: string | null;
  allDay: boolean;
  organizer: string | null;
  scheduledFor: string | null;
  attendees: { email: string; name: string | null; response: string | null }[];
  myResponse: string | null;
  meetingLink: string | null;
  htmlLink: string | null;
  description: string;
  claireJoins: boolean;
  join: { status: 'pending' | 'joined' | 'failed' | 'skipped'; reason: string | null; meetingId: string | null } | null;
  claireInstructions: string | null;
}

export interface ClaireGoogleStatus {
  connected: boolean;
  email: string | null;
  connectedByName: string | null;
}

export interface ClaireResearchComp {
  key: string;
  source: 'ebay' | 'web' | 'store';
  site: string;
  title: string;
  price: number;
  priceKind: 'sold' | 'asking';
  condition: string | null;
  url: string | null;
  imageUrl: string | null;
  soldDate: string | null;
  foundBy: string[];
  verdict?: 'exact' | 'close' | 'different';
  confidence?: number;
  differences?: string[];
  reason?: string;
  outlier?: boolean;
}

export interface ClairePriceStats {
  count: number;
  median: number;
  low: number;
  high: number;
  p25: number;
  p75: number;
}

export interface ClaireFolderJobSku {
  sku: string;
  entry: { collection: string; id: string; title: string; status: string; imageCount: number } | null;
  uploads?: string[];
  review?: { matches: 'yes' | 'no' | 'unsure'; issues: string[]; note: string } | null;
  result?: { added: number; total: number } | null;
  submitted?: { ok: true; status: string } | { ok: false; reason: string } | null;
}

export interface ClaireFolderJob {
  id: string;
  status: 'waiting' | 'review_ready' | 'saving' | 'done' | 'cancelled';
  skus: ClaireFolderJobSku[];
  mode: 'add' | 'replace' | null;
  thenSubmit: boolean;
  thenReview: boolean;
  createdAt: string | null;
}

export interface ClaireFolderListJob {
  id: string;
  /** 'search' looks for file and folder names; 'list' reads the photo folder's folder names. */
  kind: 'list' | 'search';
  status: 'waiting' | 'done' | 'cancelled';
  count: number | null;
  folderName: string | null;
  computerOf: string | null;
  search?: { query: string; where: 'photoFolder' | 'otherFolder'; type?: 'any' | 'files' | 'folders' } | null;
  createdAt: string | null;
}

export interface ClaireResearchReport {
  id: string;
  ownerName: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  stage: string | null;
  brief: { description: string; sku: string | null; goal: 'retail' | 'buy' | 'ebay' | 'general'; condition: string | null };
  images: string[];
  profile: {
    summary: string;
    brand: string | null;
    line: string | null;
    modelNumber: string | null;
    metal: string | null;
    stones: string | null;
    size: string | null;
    keyFeatures: string[];
    uncertain: string[];
  } | null;
  comps: ClaireResearchComp[];
  rejectedCount: number;
  stats: { sold: ClairePriceStats | null; soldExact: ClairePriceStats | null; asking: ClairePriceStats | null; askingExact: ClairePriceStats | null } | null;
  counts: { ebayFound: number; web: number; webUnverifiedDropped: number; store: number; checked: number } | null;
  notes: string[];
  summary: string | null;
  error: string | null;
  sessionId: string | null;
  createdAt: string | null;
  finishedAt: string | null;
}

export interface HandsFreeReply {
  heard: string;
  text: string;
  audio: string | null;
  contentType: string | null;
  /** They said "Thanks Claire, you're good". */
  ended?: boolean;
}

/** Every privileged Claire operation goes through these callables; the app never touches tools directly. */
export const ClaireService = {
  newSessionId(): string {
    return doc(collection(db, SESSIONS)).id;
  },

  /** `images` are JPEG data URLs (see shrinkPhoto). */
  async send(text: string, sessionId: string, source: 'chat' | 'bubble', mode: ClaireMode = 'ask', images: string[] = []): Promise<ClaireReply> {
    const fn = httpsCallable<unknown, ClaireReply>(functions, 'claireSend', { timeout: 300_000 });
    return (await fn({ text, sessionId, source, mode, ...(images.length ? { images } : {}) })).data;
  },

  async research(id: string): Promise<ClaireResearchReport> {
    const res = await httpsCallable<unknown, ClaireResearchReport>(functions, 'claireUser')({ action: 'research', payload: { id } });
    return res.data;
  },

  async folderPhotos(action: 'get' | 'review' | 'attach' | 'cancel', id: string, extra: Record<string, unknown> = {}): Promise<ClaireFolderJob> {
    const res = await httpsCallable<unknown, ClaireFolderJob>(functions, 'claireFolderPhotos', { timeout: 150_000 })({ action, id, ...extra });
    return res.data;
  },

  async replies<T = unknown>(action: 'list' | 'count' | 'send' | 'cancel' | 'redraft', payload: Record<string, unknown> = {}): Promise<T> {
    const res = await httpsCallable<unknown, T>(functions, 'claireReplies', { timeout: 120_000 })({ action, ...payload });
    return res.data;
  },

  async folderList(action: 'get' | 'listing' | 'searchResults' | 'cancel', id: string, extra: Record<string, unknown> = {}): Promise<ClaireFolderListJob> {
    const res = await httpsCallable<unknown, ClaireFolderListJob>(functions, 'claireFolderPhotos', { timeout: 120_000 })({ action, id, ...extra });
    return res.data;
  },

  /** Claire's own FIN account and task schedule (Master Admin only). */
  async employee<T = any>(
    action: 'get' | 'createAccount' | 'saveSettings' | 'saveLiveChat' | 'saveTask' | 'deleteTask' | 'runNow' | 'saveTeamSettings' | 'reviewStats' | 'saveMessaging' | 'saveHandoffOptions' | 'arrange' | 'saveRoutines' | 'saveDigest' | 'sendDigest',
    payload: Record<string, unknown> = {},
  ): Promise<T> {
    const fn = httpsCallable<unknown, T>(functions, 'claireEmployee', { timeout: 60_000 });
    return (await fn({ action, payload })).data;
  },

  /** "Send to Claire": she takes these Inventory Database items and asks the sender in FIN chat what to do. */
  async sendItemsToClaire(ids: string[]): Promise<{ ok: boolean; chatId: string; count: number }> {
    const res = await httpsCallable<unknown, { ok: boolean; chatId: string; count: number }>(functions, 'claireHandoff', { timeout: 60_000 })({ ids });
    return res.data;
  },

  /** "Give Claire a task" from anywhere in FIN. */
  async assignTask(payload: {
    title: string;
    instructions: string;
    contactEmail?: string;
    when: 'asap' | 'by' | 'at';
    date?: string;
    time?: string;
  }): Promise<{ ok: boolean; id: string; when: string }> {
    const res = await httpsCallable<unknown, { ok: boolean; id: string; when: string }>(functions, 'claireAssign', { timeout: 30_000 })(payload);
    return res.data;
  },

  async forgetMemory(id: string): Promise<void> {
    await httpsCallable(functions, 'claireUser')({ action: 'forgetMemory', payload: { id } });
  },

  /** Claire's avatar from Master Claire → Identity & voice, or null if none is set. */
  async avatarUrl(): Promise<string | null> {
    const res = await httpsCallable<unknown, { avatarUrl: string | null }>(functions, 'claireUser')({ action: 'avatar' });
    return res.data.avatarUrl;
  },

  async watchAction(action: 'authorizeWatch' | 'pauseWatch' | 'endWatch', id: string): Promise<void> {
    await httpsCallable(functions, 'claireUser')({ action, payload: { id } });
  },

  /** Speaks text in Claire's ElevenLabs voice. */
  async speak(text: string): Promise<HTMLAudioElement> {
    const { audio, contentType } = (await voiceFn()({ action: 'speak', payload: { text } })).data;
    return playBase64(audio, contentType);
  },

  /** Hands-free turn: spoken question in, Claire's written and spoken answer out. */
  async handsFree(payload: { audio: string; mimeType: string; sessionId: string; shared: boolean }): Promise<HandsFreeReply> {
    const fn = httpsCallable<unknown, HandsFreeReply>(functions, 'claireHandsFree', { timeout: 300_000 });
    return (await fn(payload)).data;
  },

  async previewVoice(voice: PresenceConfig['voice'], text?: string): Promise<HTMLAudioElement> {
    const { audio, contentType } = (await voiceFn()({ action: 'preview', payload: { voice, text } })).data;
    return playBase64(audio, contentType);
  },

  async listVoices(): Promise<ElevenVoice[]> {
    return (await voiceFn()({ action: 'listVoices' })).data.voices;
  },

  async joinMeeting(
    meetingUrl: string,
    opts: { outsideZoom?: boolean; brief?: string; participation?: MeetingParticipation | null } = {},
  ): Promise<{ id: string; canSpeak: boolean; imageUsed: boolean }> {
    const fn = httpsCallable<unknown, { id: string; canSpeak: boolean; imageUsed: boolean }>(functions, 'claireMeeting', { timeout: 120_000 });
    return (await fn({ action: 'join', payload: { meetingUrl, ...opts } })).data;
  },

  async zoom(action: 'status' | 'start' | 'disconnect'): Promise<ZoomStatus & { url?: string }> {
    const fn = httpsCallable<unknown, ZoomStatus & { url?: string }>(functions, 'claireZoomConnect', { timeout: 30_000 });
    return (await fn({ action })).data;
  },

  /** Claire's own Google Calendar (Master Admin only). */
  async claireGoogle<T = ClaireGoogleStatus>(action: 'status' | 'start' | 'disconnect' | 'events' | 'setJoin', payload: Record<string, unknown> = {}): Promise<T> {
    const fn = httpsCallable<unknown, T>(functions, 'claireGoogleConnect', { timeout: 60_000 });
    return (await fn({ action, ...payload })).data;
  },

  async leaveMeeting(id: string): Promise<void> {
    await httpsCallable(functions, 'claireMeeting', { timeout: 60_000 })({ action: 'leave', payload: { id } });
  },

  subscribeMyMeetings(uid: string, cb: (m: ClaireMeeting[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireMeetings'), where('ownerUid', '==', uid), limit(50)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClaireMeeting).sort((a, b) => byMillis(b.createdAt) - byMillis(a.createdAt))),
      () => cb([]),
    );
  },

  subscribeMeetings(cb: (m: ClaireMeeting[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireMeetings'), orderBy('createdAt', 'desc'), limit(200)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClaireMeeting)),
      () => cb([]),
    );
  },

  subscribeMeetingTranscript(id: string, cb: (l: MeetingLine[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireMeetings', id, 'transcript'), orderBy('createdAt', 'asc'), limit(3000)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as MeetingLine)),
      () => cb([]),
    );
  },

  subscribeMyIdentities(uid: string, cb: (i: ClaireIdentity[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireIdentities'), where('uid', '==', uid), limit(20)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClaireIdentity)),
      () => cb([]),
    );
  },

  subscribeMyWatches(uid: string, cb: (w: ClaireWatch[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireWatches'), where('ownerUid', '==', uid), limit(100)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClaireWatch).sort((a, b) => byMillis(b.createdAt) - byMillis(a.createdAt))),
      () => cb([]),
    );
  },

  async admin<T = any>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
    const fn = httpsCallable<unknown, T>(functions, 'claireAdmin', { timeout: 60_000 });
    return (await fn({ action, payload })).data;
  },

  subscribeSessions(uid: string, cb: (s: ClaireSession[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, SESSIONS), where('userId', '==', uid), limit(100)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClaireSession).sort((a, b) => byMillis(b.updatedAt) - byMillis(a.updatedAt))),
      () => cb([]),
    );
  },

  subscribeSession(id: string, cb: (s: ClaireSession | null) => void): Unsubscribe {
    return retrying((onError) =>
      onSnapshot(doc(db, SESSIONS, id), (snap) => cb(snap.exists() ? ({ id: snap.id, ...snap.data() } as ClaireSession) : null), onError),
    );
  },

  subscribeMessages(sessionId: string, cb: (m: ClaireMessage[]) => void): Unsubscribe {
    return retrying((onError) =>
      onSnapshot(
        query(collection(db, SESSIONS, sessionId, 'messages'), orderBy('createdAt', 'asc'), limit(300)),
        (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClaireMessage)),
        onError,
      ),
    );
  },

  subscribeMyMemory(uid: string, cb: (m: MemoryEntry[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireMemory'), where('ownerUid', '==', uid), limit(200)),
      (snap) =>
        cb(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }) as MemoryEntry)
            .filter((m) => m.status === 'active' && m.scope === 'user')
            .sort((a, b) => byMillis(b.createdAt) - byMillis(a.createdAt)),
        ),
      () => cb([]),
    );
  },

  /* ── Master Claire ── */

  subscribeActivity(cb: (a: ClaireActivity[]) => void, max = 500): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireActivity'), orderBy('timestamp', 'desc'), limit(max)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClaireActivity)),
      () => cb([]),
    );
  },

  subscribeBrainVersions(cb: (v: BrainVersion[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireBrainVersions'), orderBy('version', 'desc'), limit(200)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as BrainVersion)),
      () => cb([]),
    );
  },

  subscribeSkills(cb: (s: ClaireSkill[]) => void): Unsubscribe {
    return onSnapshot(
      collection(db, 'claireSkills'),
      (snap) => cb(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as ClaireSkill).sort((a, b) => a.name.localeCompare(b.name))),
      () => cb([]),
    );
  },

  subscribeKnowledge(cb: (k: KnowledgeEntry[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireKnowledge'), limit(1000)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as KnowledgeEntry).sort((a, b) => byMillis(b.updatedAt) - byMillis(a.updatedAt))),
      () => cb([]),
    );
  },

  subscribeCompanyMemory(cb: (m: MemoryEntry[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireMemory'), where('scope', '==', 'company'), limit(500)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as MemoryEntry).sort((a, b) => byMillis(b.createdAt) - byMillis(a.createdAt))),
      () => cb([]),
    );
  },

  subscribeKnowledgeVersions(id: string, cb: (v: KnowledgeVersion[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireKnowledge', id, 'versions'), orderBy('version', 'desc'), limit(100)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as KnowledgeVersion)),
      () => cb([]),
    );
  },

  subscribeWatches(cb: (w: ClaireWatch[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireWatches'), orderBy('createdAt', 'desc'), limit(300)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClaireWatch)),
      () => cb([]),
    );
  },

  subscribeIdentities(cb: (i: ClaireIdentity[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireIdentities'), limit(500)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClaireIdentity)),
      () => cb([]),
    );
  },

  subscribeApprovals(cb: (a: ClaireApproval[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireApprovals'), where('status', '==', 'pending'), limit(200)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClaireApproval)),
      () => cb([]),
    );
  },

  /** Actions Claire asked this person to confirm in one conversation. */
  subscribeSessionApprovals(uid: string, sessionId: string, cb: (a: ClaireApproval[]) => void): Unsubscribe {
    return onSnapshot(
      query(collection(db, 'claireApprovals'), where('requestedBy', '==', uid), where('sessionId', '==', sessionId), limit(50)),
      (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ClaireApproval).sort((a, b) => byMillis(a.createdAt) - byMillis(b.createdAt))),
      () => cb([]),
    );
  },

  async decideApproval(id: string, decision: 'approve' | 'reject'): Promise<{ status: string; message: string }> {
    const fn = httpsCallable<unknown, { status: string; message: string }>(functions, 'claireApprove', { timeout: 120_000 });
    return (await fn({ id, decision })).data;
  },
};
